import React, { useState, useEffect } from "react";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardCheckIcon,
  CodeIcon,
  CreditCardIcon,
  DocumentAddIcon,
  HomeIcon,
  KeyIcon,
  TruckIcon,
  UserIcon,
} from "@heroicons/react/solid";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { nightOwl } from "react-syntax-highlighter/dist/esm/styles/prism";
import BookingFormOneway from "./resources/oneway.jpg";
// import BookingFormOneway from "./resources/BookingForm.jpg";
import BookingFormHourly from "./resources/hourly.jpg";
import BookingSummary from "./resources/booking-summary.jpg";
import VehicleSelection from "./resources/select-vehicles.jpg";
import Passenger from "./resources/passenger-details.jpg";
import Payment from "./resources/payment-method.jpg";
import Intro from "./resources/all.jpg";

const Documentation = () => {
  const [activeSection, setActiveSection] = useState("introduction");
  const [expandedSection, setExpandedSection] = useState("bookingForm");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const CodeSnippet = ({ code }) => {
    const customStyle = {
      background: "#282c34", // Custom background color
      color: "#9CDCFE", // Custom text color
      padding: "20px", // Padding around the code block
      borderRadius: "5px", // Rounded corners for the code block
      lineHeight: "1.5", // Line height for better readability
      overflow: "auto", // Enables scrolling for overflow content
      maxHeight: "500px", // Maximum height before scrolling starts
    };
    return (
      <SyntaxHighlighter
        language="json"
        style={nightOwl}
        customStyle={customStyle}
        wrapLines={true}
        wrapLongLines={true}
      >
        {code}
      </SyntaxHighlighter>
    );
  };

  const formatContent = (section) => {
    if (section === "credentials") {
      return (
        <div className="">
          <div className="space-y-6">
            <p className="text-gray-700 leading-relaxed">
              To access our booking system, you'll need to obtain API
              credentials which includes the email and the api key which will be
              provided from our end. Contact us to request your unique API key
              and Email. Keep these credentials secure.
            </p>

            <div className="space-y-4">
              <div
                className="rounded-lg overflow-hidden"
                style={{
                  background: "#282c34",
                  color: "#9CDCFE",
                  padding: "20px",
                  borderRadius: "8px",
                  lineHeight: "1.5",
                  maxHeight: "500px",
                  overflow: "auto",
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-semibold">
                    Credentials Format
                  </h4>
                  <button className="text-gray-400 hover:text-white transition-colors">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
                <pre
                  style={{
                    margin: 0,
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: "14px",
                  }}
                >
                  <code>
                    <span style={{ color: "#C792EA" }}>{`{`}</span>
                    {"\n  "}
                    <span style={{ color: "#F07178" }}>"apiKey"</span>
                    <span style={{ color: "#C792EA" }}>:</span>{" "}
                    <span style={{ color: "#C3E88D" }}>
                      "your-api-key-here"
                    </span>
                    <span style={{ color: "#C792EA" }}>,</span>
                    {"\n  "}
                    <span style={{ color: "#F07178" }}>"email"</span>
                    <span style={{ color: "#C792EA" }}>:</span>{" "}
                    <span style={{ color: "#C3E88D" }}>
                      "your-registered-email@example.com"
                    </span>
                    <span style={{ color: "#C792EA" }}>,</span>
                    {"\n  "}
                    <span style={{ color: "#F07178" }}>"brandColor"</span>
                    <span style={{ color: "#C792EA" }}>:</span>{" "}
                    <span style={{ color: "#C3E88D" }}>"#FF5733"</span>
                    <span style={{ color: "#C792EA" }}>,</span>
                    {"\n  "}
                    <span style={{ color: "#F07178" }}>
                      "bookingConditionsPage"
                    </span>
                    <span style={{ color: "#C792EA" }}>:</span>{" "}
                    <span style={{ color: "#C3E88D" }}>
                      "https://yourdomain.com/booking-conditions"
                    </span>
                    <span style={{ color: "#C792EA" }}>,</span>
                    {"\n  "}
                    <span style={{ color: "#F07178" }}>
                      "privacyPolicyPage"
                    </span>
                    <span style={{ color: "#C792EA" }}>:</span>{" "}
                    <span style={{ color: "#C3E88D" }}>
                      "https://yourdomain.com/privacy-policy"
                    </span>
                    <span style={{ color: "#C792EA" }}>,</span>
                    {"\n  "}
                    <span style={{ color: "#F07178" }}>
                      "termsAndConditionsPage"
                    </span>
                    <span style={{ color: "#C792EA" }}>:</span>{" "}
                    <span style={{ color: "#C3E88D" }}>
                      "https://yourdomain.com/terms-and-conditions"
                    </span>
                    {"\n"}
                    <span style={{ color: "#C792EA" }}>{`}`}</span>
                  </code>
                </pre>
              </div>
              <p className="text-sm text-gray-500 italic">
                Example of how your credentials will be provided
              </p>
            </div>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-yellow-600 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-yellow-800 font-semibold">
                    API Whitelisting Required
                  </h4>
                  <p className="text-yellow-700 text-sm mt-1">
                    For security purposes, your domain must be whitelisted
                    before API requests will be accepted. Please provide your
                    domain(s) to our support team during the credential setup
                    process.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (section === "howToIntegrate") {
      return (
        <div className="space-y-6">
          <p className="text-gray-700 leading-relaxed">
            Integration with our booking system is straightforward and can be
            completed in just a few steps.The System is flexible and can be
            integrated with all type of system.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800">
                Integration Steps
              </h3>

              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-800">
                  CDN Integration
                </h3>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      Initialize the Widget
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Add the following script to configure and mount the
                      white-label widget using your credentials.
                    </p>
                    <pre className="text-xs bg-gray-100 text-gray-800 p-2 rounded mt-2 whitespace-pre-wrap break-words">
                      {`
      <link
      rel="stylesheet"
      href=" https://cdn.jsdelivr.net/gh/drivado-org/wl@latest/drivado-wl.min.css"
    />
    <script>
      // Create a Script Tag
      const script = document.createElement("script");

      // Specify the JS CDN of White Label      
      script.src =
        " https://cdn.jsdelivr.net/gh/drivado-org/wl@latest/drivado-wl.min.js";

      // Function which needs to be executed when script is loded
      script.onload = function () {
        // Initiate the White Label
        window.Drivado.init({
          apiKey: "-----API kEY-----",
          email: "-----EMAIL-----",
          brandColor: "----COLOUR IN HEX-----", // expected in hex Code
          bookingConditionsPage: "https://yourdomain.com/booking-conditions",
          privacyPolicyPage: "https://yourdomain.com/privacy-policy", 
          termsAndConditionsPage: "https://yourdomain.com/terms-and-conditions"
        });

        // DOM Node where Form will be mounted
        // NB: init function should be first than any
        // other function to get all the functionality

        window.Drivado.mountBookingSearchForm({
          containerId: "booking-search-form",
        });
      };

      // Attach it to the Body Tag
      document.body.appendChild(script);
    </script>`}
                    </pre>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">The CSS Tag</h4>
                    <p className="text-gray-600 text-sm">
                      This is the following link tag that have been added in the
                      above to load the default styling for the widget.
                      Different link tags are provided for test environment and
                      for production environment. Based on the environment the
                      different tags will be replaced accordingly.
                    </p>

                    <h4 className="pt-2.5 font-medium text-gray-800">
                      Test Environment
                    </h4>
                    <pre className="text-xs bg-gray-100 text-gray-800 p-2 rounded mt-2 whitespace-pre-wrap break-words">
                      {`<link href="https://cdn.jsdelivr.net/gh/drivado-org/wl-test@9e130d0164a6ba7355568d4d1d644673a9696647/drivado-wl.min.css" rel="stylesheet"/>`}
                    </pre>

                    <h4 className="pt-2.5 font-medium text-gray-800">
                      Production Environment
                    </h4>
                    <pre className="text-xs bg-gray-100 text-gray-800 p-2 rounded mt-2 whitespace-pre-wrap break-words">
                      {`<link href=" https://cdn.jsdelivr.net/gh/drivado-org/wl-test@fa650dbc68b56dc5f51b561cfa369ad1c5a99ca6/drivado-wl.min.css" rel="stylesheet" />`}
                    </pre>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      The JavaScript Tag
                    </h4>
                    <p className="text-gray-600 text-sm">
                      This is the following link tag that have been added in the
                      above to load the widget functionality. Different link
                      tags are provided for test environment and for production
                      environment. Based on the environment the different tags
                      will be replaced accordingly.
                    </p>
                    <h4 className="pt-2.5 font-medium text-gray-800">
                      Test Environment
                    </h4>
                    <pre className="text-xs bg-gray-100 text-gray-800 p-2 rounded mt-2 whitespace-pre-wrap break-words">
                      {`<script src="https://cdn.jsdelivr.net/gh/drivado-org/wl-test@9e130d0164a6ba7355568d4d1d644673a9696647/drivado-wl.min.js"></script>`}
                    </pre>

                    <h4 className="pt-2.5 font-medium text-gray-800">
                      Production Environment
                    </h4>
                    <pre className="text-xs bg-gray-100 text-gray-800 p-2 rounded mt-2 whitespace-pre-wrap break-words">
                      {`<script src="https://cdn.jsdelivr.net/gh/drivado-org/wl-test@fa650dbc68b56dc5f51b561cfa369ad1c5a99ca6/drivado-wl.min.js"></script>`}
                    </pre>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      Container Id Instruction
                    </h4>
                    <p className="text-gray-600 text-sm">
                      The{" "}
                      <span className="text-xs font-semibold rounded-md bg-gray-100 text-gray-800 p-1">
                        containerId
                      </span>{" "}
                      should match the id of the HTML element where the Form
                      will be mounted.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    5
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Instruction</h4>
                    <p className="text-gray-600 text-sm">
                      The CDN and the CSS must be attached at the end of the
                      body tag
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-800">
                  Global TypeScript Integration
                </h3>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      Global Typescript Function
                    </h4>
                    <p className="text-gray-600 text-sm">
                      For TypeScript-based projects, the following configuration
                      can be defined globally to initialize the given code
                      across your application.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-semibold">CDN Integration</h4>
                  <button className="text-gray-400 hover:text-white transition-colors">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
                <div
                  style={{
                    backgroundColor: "#1e1e1e",
                    borderRadius: "8px",
                    padding: "20px",
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: "14px",
                    lineHeight: "1.5",
                    overflow: "auto",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #333",
                  }}
                >
                  <pre style={{ margin: 0 }}>
                    <code>
                      <span style={{ color: "#FF7F50" }}>{"<link"}</span>
                      {"\n  "}
                      <span style={{ color: "#82AAFF" }}>rel</span>
                      <span style={{ color: "#C792EA" }}>="</span>
                      <span style={{ color: "#C3E88D" }}>stylesheet</span>
                      <span style={{ color: "#C792EA" }}>"</span>
                      {"\n  "}
                      <span style={{ color: "#82AAFF" }}>href</span>
                      <span style={{ color: "#C792EA" }}>="</span>
                      <span style={{ color: "#C3E88D" }}>
                        https://cdn.jsdelivr.net/gh/drivado-org/wl@latest/drivado-wl.min.css
                      </span>
                      <span style={{ color: "#C792EA" }}>"</span>
                      {"\n"}
                      <span style={{ color: "#FF7F50" }}>{"</"}</span>
                      <span style={{ color: "#FF7F50" }}>link</span>
                      <span style={{ color: "#FF7F50" }}>{">"}</span>
                      {"\n"}
                      <span style={{ color: "#FF7F50" }}>{"<script>"}</span>
                      {"\n  "}
                      <span style={{ color: "#546E7A" }}>
                        // Create a Script Tag
                      </span>
                      {"\n  "}
                      <span style={{ color: "#C792EA" }}>const</span>{" "}
                      <span style={{ color: "#82AAFF" }}>script</span>{" "}
                      <span style={{ color: "#C792EA" }}>=</span>{" "}
                      <span style={{ color: "#82AAFF" }}>document</span>
                      <span style={{ color: "#C792EA" }}>.</span>
                      <span style={{ color: "#82AAFF" }}>createElement</span>
                      <span style={{ color: "#C792EA" }}>(</span>
                      <span style={{ color: "#C3E88D" }}>"script"</span>
                      <span style={{ color: "#C792EA" }}>);</span>
                      {"\n\n  "}
                      <span style={{ color: "#546E7A" }}>
                        // Specify the JS CDN of White Label
                      </span>
                      {"\n  "}
                      <span style={{ color: "#82AAFF" }}>script</span>
                      <span style={{ color: "#C792EA" }}>.</span>
                      <span style={{ color: "#82AAFF" }}>src</span>{" "}
                      <span style={{ color: "#C792EA" }}>=</span>
                      {"\n    "}
                      <span style={{ color: "#C3E88D" }}>
                        "https://cdn.jsdelivr.net/gh/drivado-org/wl@latest/drivado-wl.min.js"
                      </span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n\n  "}
                      <span style={{ color: "#546E7A" }}>
                        // Function which needs to be executed when script is
                        loaded
                      </span>
                      {"\n  "}
                      <span style={{ color: "#82AAFF" }}>script</span>
                      <span style={{ color: "#C792EA" }}>.</span>
                      <span style={{ color: "#82AAFF" }}>onload</span>{" "}
                      <span style={{ color: "#C792EA" }}>=</span>{" "}
                      <span style={{ color: "#C792EA" }}>function</span>{" "}
                      <span style={{ color: "#C792EA" }}>()</span>{" "}
                      <span style={{ color: "#C792EA" }}>{"{"}</span>
                      {"\n    "}
                      <span style={{ color: "#546E7A" }}>
                        // Initiate the White Label
                      </span>
                      {"\n    "}
                      <span style={{ color: "#82AAFF" }}>window</span>
                      <span style={{ color: "#C792EA" }}>.</span>
                      <span style={{ color: "#82AAFF" }}>Drivado</span>
                      <span style={{ color: "#C792EA" }}>.</span>
                      <span style={{ color: "#82AAFF" }}>init</span>
                      <span style={{ color: "#C792EA" }}>(</span>
                      <span style={{ color: "#C792EA" }}>{"{"}</span>
                      {"\n      "}
                      <span style={{ color: "#F07178" }}>apiKey</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C3E88D" }}>
                        "-----API kEY-----"
                      </span>
                      <span style={{ color: "#C792EA" }}>,</span>
                      {"\n      "}
                      <span style={{ color: "#F07178" }}>email</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C3E88D" }}>
                        "-----EMAIL-----"
                      </span>
                      <span style={{ color: "#C792EA" }}>,</span>
                      {"\n      "}
                      <span style={{ color: "#F07178" }}>brandColor</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C3E88D" }}>
                        "----COLOUR IN HEX-----"
                      </span>
                      <span style={{ color: "#C792EA" }}>,</span>{" "}
                      <span style={{ color: "#546E7A" }}>
                        // expected in hex Code
                      </span>
                      {"\n      "}
                      <span style={{ color: "#F07178" }}>
                        bookingConditionsPage
                      </span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C3E88D" }}>
                        "https://yourdomain.com/booking-conditions"
                      </span>
                      <span style={{ color: "#C792EA" }}>,</span>
                      {"\n      "}
                      <span style={{ color: "#F07178" }}>
                        privacyPolicyPage
                      </span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C3E88D" }}>
                        "https://yourdomain.com/privacy-policy"
                      </span>
                      <span style={{ color: "#C792EA" }}>,</span>
                      {"\n      "}
                      <span style={{ color: "#F07178" }}>
                        termsAndConditionsPage
                      </span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C3E88D" }}>
                        "https://yourdomain.com/terms-and-conditions"
                      </span>
                      {"\n    "}
                      <span style={{ color: "#C792EA" }}>{"}"});</span>
                      {"\n\n    "}
                      <span style={{ color: "#546E7A" }}>
                        // DOM Node where Form will be mounted
                      </span>
                      {"\n    "}
                      <span style={{ color: "#546E7A" }}>
                        // NB: init function should be first than any
                      </span>
                      {"\n    "}
                      <span style={{ color: "#546E7A" }}>
                        // other function to get all the functionality
                      </span>
                      {"\n\n    "}
                      <span style={{ color: "#82AAFF" }}>window</span>
                      <span style={{ color: "#C792EA" }}>.</span>
                      <span style={{ color: "#82AAFF" }}>Drivado</span>
                      <span style={{ color: "#C792EA" }}>.</span>
                      <span style={{ color: "#82AAFF" }}>
                        mountBookingSearchForm
                      </span>
                      <span style={{ color: "#C792EA" }}>(</span>
                      <span style={{ color: "#C792EA" }}>{"{"}</span>
                      {"\n      "}
                      <span style={{ color: "#F07178" }}>containerId</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C3E88D" }}>
                        "booking-search-form"
                      </span>
                      <span style={{ color: "#C792EA" }}>,</span>
                      {"\n    "}
                      <span style={{ color: "#C792EA" }}>{"}"});</span>
                      {"\n  "}
                      <span style={{ color: "#C792EA" }}>{"}"};;</span>
                      {"\n\n  "}
                      <span style={{ color: "#546E7A" }}>
                        // Attach it to the Body Tag
                      </span>
                      {"\n  "}
                      <span style={{ color: "#82AAFF" }}>document</span>
                      <span style={{ color: "#C792EA" }}>.</span>
                      <span style={{ color: "#82AAFF" }}>body</span>
                      <span style={{ color: "#C792EA" }}>.</span>
                      <span style={{ color: "#82AAFF" }}>appendChild</span>
                      <span style={{ color: "#C792EA" }}>(</span>
                      <span style={{ color: "#82AAFF" }}>script</span>
                      <span style={{ color: "#C792EA" }}>);</span>
                      {"\n"}
                      <span style={{ color: "#FF7F50" }}>{"</script>"}</span>
                    </code>
                  </pre>
                </div>
                {/* <pre className="text-green-400 text-sm">
                  {`
      <link
      rel="stylesheet"
      href=" https://cdn.jsdelivr.net/gh/drivado-org/wl@main/drivado-wl.min.css"
    />
    <script>
      // Create a Script Tag
      const script = document.createElement("script");

      // Specify the JS CDN of White Label      
      script.src =
        " https://cdn.jsdelivr.net/gh/drivado-org/wl@main/drivado-wl.min.js";

      // Function which needs to be executed when script is loded
      script.onload = function () {
        // Initiate the White Label
        window.Drivado.init({
          apiKey: "-----API kEY-----",
          email: "-----EMAIL-----",
          brandColor: "----COLOUR IN HEX-----", // expected in hex Code
          bookingConditionsPage: "https://yourdomain.com/booking-conditions",
          privacyPolicyPage: "https://yourdomain.com/privacy-policy", 
          termsAndConditionsPage: "https://yourdomain.com/terms-and-conditions"
        });

        // DOM Node where Form will be mounted
        // NB: init function should be first than any
        // other function to get all the functionality

        window.Drivado.mountBookingSearchForm({
          containerId: "booking-search-form",
        });
      };

      // Attach it to the Body Tag
      document.body.appendChild(script);
    </script>`}
                </pre> */}
              </div>

              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-semibold">
                    Global TypeScript Integration
                  </h4>
                  <button className="text-gray-400 hover:text-white transition-colors">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>

                {/* <pre className="text-green-400 text-sm">
                  {`// Global Type of the White Label
declare global {
  interface GlobalConfig {
    email: string;
    apiKey: string;
    brandColor: string;
    privacyPolicyPage: string;
    bookingConditionsPage: string;
    termsAndConditionsPage: string;
  }

  interface MountParams {
    containerId: string;
  }

  interface Window {
    Drivado: {
      init: (config: GlobalConfig) => void;
      mountBookingSearchForm: (props: MountParams) => void;
    };
  }
}`}
                </pre> */}
                <div
                  style={{
                    backgroundColor: "#1e1e1e",
                    borderRadius: "8px",
                    padding: "20px",
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: "14px",
                    lineHeight: "1.5",
                    overflow: "auto",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #333",
                  }}
                >
                  <pre style={{ margin: 0 }}>
                    <code>
                      <span style={{ color: "#546E7A" }}>
                        // Global Type of the White Label
                      </span>
                      {"\n"}
                      <span style={{ color: "#C792EA" }}>declare</span>{" "}
                      <span style={{ color: "#C792EA" }}>global</span>{" "}
                      <span style={{ color: "#C792EA" }}>{"{"}</span>
                      {"\n  "}
                      <span style={{ color: "#C792EA" }}>interface</span>{" "}
                      <span style={{ color: "#FFCB6B" }}>GlobalConfig</span>{" "}
                      <span style={{ color: "#C792EA" }}>{"{"}</span>
                      {"\n    "}
                      <span style={{ color: "#F07178" }}>email</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#82AAFF" }}>string</span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n    "}
                      <span style={{ color: "#F07178" }}>apiKey</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#82AAFF" }}>string</span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n    "}
                      <span style={{ color: "#F07178" }}>brandColor</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#82AAFF" }}>string</span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n    "}
                      <span style={{ color: "#F07178" }}>
                        privacyPolicyPage
                      </span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#82AAFF" }}>string</span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n    "}
                      <span style={{ color: "#F07178" }}>
                        bookingConditionsPage
                      </span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#82AAFF" }}>string</span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n    "}
                      <span style={{ color: "#F07178" }}>
                        termsAndConditionsPage
                      </span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#82AAFF" }}>string</span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n  "}
                      <span style={{ color: "#C792EA" }}>{"}"}</span>
                      {"\n\n  "}
                      <span style={{ color: "#C792EA" }}>interface</span>{" "}
                      <span style={{ color: "#FFCB6B" }}>MountParams</span>{" "}
                      <span style={{ color: "#C792EA" }}>{"{"}</span>
                      {"\n    "}
                      <span style={{ color: "#F07178" }}>containerId</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#82AAFF" }}>string</span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n  "}
                      <span style={{ color: "#C792EA" }}>{"}"}</span>
                      {"\n\n  "}
                      <span style={{ color: "#C792EA" }}>interface</span>{" "}
                      <span style={{ color: "#FFCB6B" }}>Window</span>{" "}
                      <span style={{ color: "#C792EA" }}>{"{"}</span>
                      {"\n    "}
                      <span style={{ color: "#F07178" }}>Drivado</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C792EA" }}>{"{"}</span>
                      {"\n      "}
                      <span style={{ color: "#F07178" }}>init</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C792EA" }}>(</span>
                      <span style={{ color: "#F07178" }}>config</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#FFCB6B" }}>GlobalConfig</span>
                      <span style={{ color: "#C792EA" }}>)</span>{" "}
                      <span style={{ color: "#C792EA" }}>{`=>`}</span>{" "}
                      <span style={{ color: "#82AAFF" }}>void</span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n      "}
                      <span style={{ color: "#F07178" }}>
                        mountBookingSearchForm
                      </span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#C792EA" }}>(</span>
                      <span style={{ color: "#F07178" }}>props</span>
                      <span style={{ color: "#C792EA" }}>:</span>{" "}
                      <span style={{ color: "#FFCB6B" }}>MountParams</span>
                      <span style={{ color: "#C792EA" }}>)</span>{" "}
                      <span style={{ color: "#C792EA" }}>{"=>"}</span>{" "}
                      <span style={{ color: "#82AAFF" }}>void</span>
                      <span style={{ color: "#C792EA" }}>;</span>
                      {"\n    "}
                      <span style={{ color: "#C792EA" }}>{`{"}"};}`}</span>
                      {"\n  "}
                      <span style={{ color: "#C792EA" }}>{"}"}</span>
                      {"\n"}
                      <span style={{ color: "#C792EA" }}>{"}"}</span>
                    </code>
                  </pre>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-rose-800 mb-3">
              Support & Resources
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg
                    className="w-6 h-6 text-rose-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-rose-700">Documentation</h4>
                <p className="text-rose-600 text-sm">Complete API reference</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg
                    className="w-6 h-6 text-rose-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-rose-700">24/7 Support</h4>
                <p className="text-rose-600 text-sm">Technical assistance</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg
                    className="w-6 h-6 text-rose-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-rose-700">Code Examples</h4>
                <p className="text-rose-600 text-sm">Sample implementations</p>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (section === "oneway") {
      return (
        <div className="space-y-3">
          <p className="mb-4">
            Complete the following details for a one-way trip booking:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">Origin Location (From):</span>{" "}
                Search and select from available cities, airports, or hotels
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">Destination (To):</span> Search
                and select from available cities, airports, or hotels
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">Travel Date (Date):</span>{" "}
                Select your preferred travel date
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">Travel Time (Time):</span>{" "}
                Specify your desired pickup time
                <ul className="mt-2 ml-6 space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <div className="h-1.5 w-1.5 rounded-full bg-gray-400 mt-1.5 mr-2"></div>
                    <div>
                      Time will automatically set based on pickup location which
                      can further be changed based on convenience
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-1.5 w-1.5 rounded-full bg-gray-400 mt-1.5 mr-2"></div>
                    <div>
                      The booking date and time should be at least 24hr prior to
                      travel date and time
                    </div>
                  </li>
                </ul>
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">
                  Preferred Currency (Currency):
                </span>{" "}
                Choose your billing currency by country or currency code
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">
                  Number of Passengers (Pax):
                </span>{" "}
                Select between <span className="font-medium">1-5</span>{" "}
                passengers (Max passenger allowed - 5 pax)
              </div>
            </li>
          </ul>
        </div>
      );
    } else if (section === "hourly") {
      return (
        <div className="space-y-3">
          <p className="mb-4">
            Complete the following details for an hourly booking:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">Origin Location (From):</span>{" "}
                Search and select from available cities, airports, or hotels
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">Duration:</span> Search and
                select travel duration within{" "}
                <span className="font-medium">
                  01 hour/20km to 24 hours/480km
                </span>
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">Travel Date (Date):</span>{" "}
                Select your preferred travel date
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">Travel Time (Time):</span>{" "}
                Specify your desired pickup time
                <ul className="mt-2 ml-6 space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <div className="h-1.5 w-1.5 rounded-full bg-gray-400 mt-1.5 mr-2"></div>
                    <div>
                      Time will automatically set based on pickup location which
                      can further be changed based on convenience
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-1.5 w-1.5 rounded-full bg-gray-400 mt-1.5 mr-2"></div>
                    <div>
                      The booking date and time should be at least 24hr prior to
                      travel date and time
                    </div>
                  </li>
                </ul>
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">
                  Preferred Currency (Currency):
                </span>{" "}
                Choose your billing currency by country or currency code
              </div>
            </li>
            <li className="flex items-start">
              <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-white text-xs">→</span>
              </div>
              <div>
                <span className="font-semibold">
                  Number of Passengers (Pax):
                </span>{" "}
                Select between <span className="font-medium">1-5</span>{" "}
                passengers
              </div>
            </li>
          </ul>
        </div>
      );
    } else if (section === "vehicleSelection") {
      return (
        <div className="space-y-4">
          <p>The booking details section displays your selections:</p>
          <div className="p-4 bg-gray-50 rounded-lg mb-4">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <li className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-rose-500 mr-2"></span>
                <span className="font-medium">Booking type:</span>{" "}
                One-way/Hourly
              </li>
              <li className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-rose-500 mr-2"></span>
                <span className="font-medium">Source:</span> Pickup location
              </li>
              <li className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-rose-500 mr-2"></span>
                <span className="font-medium">
                  Destination (Only for Oneway):
                </span>{" "}
                Drop-off location
              </li>
              <li className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-rose-500 mr-2"></span>
                <span className="font-medium">Distance:</span> Trip distance
              </li>
              <li className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-rose-500 mr-2"></span>
                <span className="font-medium">Duration:</span> Trip duration
              </li>
              <li className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-rose-500 mr-2"></span>
                <span className="font-medium">Travel date:</span> Selected
                pickup date
              </li>
              <li className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-rose-500 mr-2"></span>
                <span className="font-medium">Travel time:</span> Selected
                pickup time
              </li>
              <li className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-rose-500 mr-2"></span>
                <span className="font-medium">Currency:</span> Selected billing
                currency
              </li>
              <li className="flex items-center">
                <span className="h-2 w-2 rounded-full bg-rose-500 mr-2"></span>
                <span className="font-medium">Number of Passengers:</span>{" "}
                Selected passenger count
              </li>
            </ul>
          </div>

          <p className="font-medium text-lg">
            Choose your vehicle from these five categories:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="p-4 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-lg mb-2">Standard Sedan</h4>
              <p className="text-gray-600 text-sm">
                Economical and comfortable for 1-3 passengers
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-lg mb-2">Premium Sedan</h4>
              <p className="text-gray-600 text-sm">
                Enhanced comfort for 1-3 passengers
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-lg mb-2">Luxury Sedan</h4>
              <p className="text-gray-600 text-sm">
                Premium experience for 1-3 passengers
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-lg mb-2">Economy Van</h4>
              <p className="text-gray-600 text-sm">
                Spacious option for 4-5 passengers
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-lg mb-2">Premium Van</h4>
              <p className="text-gray-600 text-sm">
                Luxury option for 4-5 passengers
              </p>
            </div>
          </div>
        </div>
      );
    } else if (section === "passengerDetails") {
      return (
        <div className="space-y-4">
          <p className="font-medium text-lg">
            Please complete the following passenger information:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div className="space-y-3">
              <h4 className="font-semibold">Required Fields:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <span className="text-white text-xs">→</span>
                  </div>
                  <div>Select your salutation</div>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <span className="text-white text-xs">→</span>
                  </div>
                  <div>
                    First name <span className="text-rose-500">*</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <span className="text-white text-xs">→</span>
                  </div>
                  <div>
                    Last name <span className="text-rose-500">*</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <span className="text-white text-xs">→</span>
                  </div>
                  <div>
                    Contact number with country code{" "}
                    <span className="text-rose-500">*</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <span className="text-white text-xs">→</span>
                  </div>
                  <div>
                    Email address <span className="text-rose-500">*</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <span className="text-white text-xs">→</span>
                  </div>
                  <div>
                    Reference number <span className="text-rose-500">*</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold">Optional Fields:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-gray-300 flex items-center justify-center mt-0.5 mr-2">
                    <span className="text-white text-xs">→</span>
                  </div>
                  <div>Flight number</div>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-gray-300 flex items-center justify-center mt-0.5 mr-2">
                    <span className="text-white text-xs">→</span>
                  </div>
                  <div>Special requests</div>
                </li>
              </ul>

              <div className="mt-6 p-4 bg-rose-50 rounded-lg border border-rose-100">
                <h4 className="font-semibold text-rose-700 mb-2">
                  Booking Summary
                </h4>
                <p className="text-gray-600 text-sm">
                  You can see the summary of your booking, including:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-gray-600">
                  <li className="flex items-center">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-400 mr-2"></span>
                    Vehicle details
                  </li>
                  <li className="flex items-center">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-400 mr-2"></span>
                    Booking information
                  </li>
                  <li className="flex items-center">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-400 mr-2"></span>
                    Pricing
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start">
              <div className="mt-1 mr-2">
                <div className="h-5 w-5 rounded border-2 border-rose-500"></div>
              </div>
              <div>
                <p className="font-medium">
                  You must accept the terms and conditions, booking conditions,
                  and privacy policy
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h4 className="font-semibold mb-2">Actions:</h4>
            <div className="flex items-center space-x-4 mt-2">
              <div className="px-4 py-2 bg-rose-500 text-white rounded-lg shadow-sm hover:bg-rose-600 transition cursor-pointer">
                Confirm Booking
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Click to proceed to the booking summary
            </p>
          </div>
        </div>
      );
    } else if (section === "bookingSummary") {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-rose-50 rounded-lg border border-rose-100">
            <p className="italic">
              Please review the following details carefully before proceeding
              with payment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div className="p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
              <h4 className="font-semibold text-lg mb-3 pb-2 border-b border-gray-200 flex items-center">
                <div className="h-4 w-4 rounded-full bg-rose-500 mr-2"></div>
                Booking Summary Details
              </h4>
              <div className="space-y-2 text-gray-700">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Booking Type:</div>
                  <div className="font-medium">One-way</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Source:</div>
                  <div className="font-medium">Airport Terminal 1</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Destination:</div>
                  <div className="font-medium">City Center Hotel</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Date & Time:</div>
                  <div className="font-medium">May 20, 2025, 10:00 AM</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Vehicle:</div>
                  <div className="font-medium">Premium Sedan</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Price:</div>
                  <div className="font-medium">$75.00</div>
                </div>
              </div>
            </div>

            <div className="p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
              <h4 className="font-semibold text-lg mb-3 pb-2 border-b border-gray-200 flex items-center">
                <div className="h-4 w-4 rounded-full bg-rose-500 mr-2"></div>
                Passenger Details
              </h4>
              <div className="space-y-2 text-gray-700">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Name:</div>
                  <div className="font-medium">John Smith</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Contact:</div>
                  <div className="font-medium">+1 234-567-8900</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Email:</div>
                  <div className="font-medium">john.smith@example.com</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Reference:</div>
                  <div className="font-medium">ABC123</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Flight Number:</div>
                  <div className="font-medium">BA287</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h4 className="font-semibold mb-2">Actions:</h4>
            <div className="flex flex-wrap gap-4 mt-2">
              <div className="px-4 py-2 bg-rose-500 text-white rounded-lg shadow-sm hover:bg-rose-600 transition cursor-pointer">
                Confirm & Pay Now
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Click to proceed to the secure payment gateway to finalize your
              booking
            </p>
          </div>
        </div>
      );
    } else if (section === "razorpayPayment") {
      return (
        <div className="space-y-4">
          <div className="p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
            <h4 className="font-semibold mb-4">
              The payment process involves these simple steps:
            </h4>

            <ol className="space-y-6">
              <li className="flex">
                <div className="h-7 w-7 rounded-full bg-rose-500 flex items-center justify-center text-white font-medium mr-3 mt-0.5 flex-shrink-0">
                  1
                </div>
                <div>
                  <p className="font-medium text-gray-800">
                    During the Checkout Process
                  </p>
                  <p className="text-gray-600 mt-1">
                    When you proceed to pay, you will be redirected to Payment
                    Gateway interface. This interface will present you with
                    payment methods like:
                  </p>
                  <ul className="mt-2 ml-6 space-y-1">
                    <li className="flex items-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-rose-400 mr-2"></span>
                      Credit/Debit Cards
                    </li>
                  </ul>
                </div>
              </li>

              <li className="flex">
                <div className="h-7 w-7 rounded-full bg-rose-500 flex items-center justify-center text-white font-medium mr-3 mt-0.5 flex-shrink-0">
                  2
                </div>
                <div>
                  <p className="font-medium text-gray-800">
                    Following the On-Screen Instructions
                  </p>
                  <p className="text-gray-600 mt-1">
                    Payment Gateway will guide you through the process of
                    entering your payment information securely for your chosen
                    method.
                  </p>
                </div>
              </li>

              <li className="flex">
                <div className="h-7 w-7 rounded-full bg-rose-500 flex items-center justify-center text-white font-medium mr-3 mt-0.5 flex-shrink-0">
                  3
                </div>
                <div>
                  <p className="font-medium text-gray-800">Confirmation</p>
                  <p className="text-gray-600 mt-1">
                    Once the payment is successful, you will usually receive a
                    confirmation message on the screen and a Voucher will be
                    sent via email.
                  </p>
                </div>
              </li>
            </ol>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="font-medium text-yellow-700">Security Note</p>
              <p className="text-gray-600 mt-1">
                Please remember, never share your sensitive payment details
                (like card numbers, CVV, passwords) with anyone you don't trust
                or on unsecure platforms.
              </p>
            </div>

            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="font-medium text-red-700">Payment Failure</p>
              <p className="text-gray-600 mt-1">
                Unfortunately, if your payment was unsuccessful, you will need
                to create a new booking from the start.
              </p>
            </div>
          </div>
        </div>
      );
    } else if (section === "bookingConfirmation") {
      return (
        <div className="space-y-4">
          <div className="p-6 bg-green-50 rounded-xl border border-green-200 shadow-sm text-center">
            <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-green-800 mb-2">
              Booking Confirmed!
            </h3>
            <p className="text-gray-700">
              Thank you for your booking. You will shortly receive a detailed
              confirmation email at your registered email address.
            </p>
          </div>

          <div className="mt-6 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
            <h4 className="font-semibold text-lg mb-4 pb-2 border-b border-gray-200">
              Key Details:
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Booking ID:
                    </span>
                    <span className="ml-2 text-gray-600">DRV-12345678</span>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Date/Time:
                    </span>
                    <span className="ml-2 text-gray-600">
                      May 20, 2025, 10:00 AM
                    </span>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Source & Destination:
                    </span>
                    <span className="ml-2 text-gray-600">
                      Airport Terminal 1 to City Center Hotel
                    </span>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                      <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.9-.5l1.4-2.1a1.5 1.5 0 00-.9-2.4h-3.4a1 1 0 00-1 .8l-.5 2c0 .5-.5.8-1 .8H6a1 1 0 00-1-1H3a1 1 0 00-1 1v1a1 1 0 001 1h2a1 1 0 001-1h2.05a2.5 2.5 0 014.9 0H14a1 1 0 001-1v-1a1 1 0 00-1-1h-3.8l.8-3H15a3 3 0 013 3v8a1 1 0 01-1 1h-1.05a2.5 2.5 0 01-4.9 0h-4.1a2.5 2.5 0 01-4.9 0H3a1 1 0 01-1-1V5a1 1 0 011-1h12z" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Type of Booking:
                    </span>
                    <span className="ml-2 text-gray-600">One Way</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Distance, Duration, Pax:
                    </span>
                    <span className="ml-2 text-gray-600">
                      25km, 40min, 2 passengers
                    </span>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Passenger details:
                    </span>
                    <span className="ml-2 text-gray-600">John Smith</span>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                      <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.9-.5l1.4-2.1a1.5 1.5 0 00-.9-2.4h-3.4a1 1 0 00-1 .8l-.5 2c0 .5-.5.8-1 .8H6a1 1 0 00-1-1H3a1 1 0 00-1 1v1a1 1 0 001 1h2a1 1 0 001-1h2.05a2.5 2.5 0 014.9 0H14a1 1 0 001-1v-1a1 1 0 00-1-1h-3.8l.8-3H15a3 3 0 013 3v8a1 1 0 01-1 1h-1.05a2.5 2.5 0 01-4.9 0h-4.1a2.5 2.5 0 01-4.9 0H3a1 1 0 01-1-1V5a1 1 0 011-1h12z" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Vehicle details:
                    </span>
                    <span className="ml-2 text-gray-600">Premium Sedan</span>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Reference Number:
                    </span>
                    <span className="ml-2 text-gray-600">ABC123</span>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="h-5 w-5 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center mt-0.5 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Price details:
                    </span>
                    <span className="ml-2 text-gray-600">$75.00 (Paid)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white rounded-xl border border-gray-200 shadow-sm text-center mt-6">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Your Booking is Successful!
            </h3>
            <p className="text-gray-700">
              A confirmation with complete booking details has been sent to your
              email address.
            </p>
            <p className="text-gray-500 mt-4 text-sm">
              We appreciate your business! If you don't receive your
              confirmation email within a few minutes, please check your spam
              folder or contact our support team at{" "}
              <span className="text-rose-600">support@drivado.com</span>.
            </p>
            <div className="mt-6 p-4 bg-rose-50 rounded-lg inline-block">
              <p className="font-medium text-rose-800">
                Thank You for Your Booking!
              </p>
              <p className="text-gray-600 mt-1">
                Your payment has been successfully processed, and your booking
                is now confirmed.
              </p>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <p className="text-gray-700 leading-relaxed">
          {sectionContent[section]?.content || ""}
        </p>
      );
    }
  };

  // Define sections and their subsections
  const sections = [
    { id: "introduction", label: "Introduction", icon: "home" },
    { id: "credentials", label: "Credentials", icon: "key" },
    { id: "howToIntegrate", label: "How To Integrate", icon: "code" },
    {
      id: "bookingForm",
      label: "Booking Form",
      icon: "filePlus",
      subSections: [
        { id: "oneway", label: "Oneway" },
        { id: "hourly", label: "Hourly" },
      ],
    },
    { id: "vehicleSelection", label: "Vehicle Selection", icon: "car" },
    { id: "passengerDetails", label: "Passenger Details", icon: "user" },
    { id: "bookingSummary", label: "Booking Summary", icon: "clipboardList" },
    { id: "razorpayPayment", label: "Payment", icon: "creditCard" },
    {
      id: "bookingConfirmation",
      label: "Booking Confirmation",
      icon: "checkCircle",
    },
  ];

  // Section content with documentation - updated with imported images and orientation info
  const sectionContent = {
    introduction: {
      title: "Introduction",
      content:
        "This documentation provides a comprehensive guide to integrating and customizing our white-label solution for partners and clients. A white-label product allows you to rebrand our platform under your own business identity, giving your end users a seamless, brand-consistent experience.",
      showImage: true,
      imageUrl: Intro,
      imageOrientation: "landscape",
      imageDimensions: { width: 1477, height: 960 },
    },
    credentials: {
      title: "Credentials",
      content:
        "To access our booking system, you'll need to obtain API credentials. Contact our support team at support@drivado.com to request your unique API key and secret. Keep these credentials secure and never share them in client-side code or public repositories. Your credentials grant access to our entire booking ecosystem, allowing you to process reservations securely.",
      showImage: false,
      imageUrl: "",
      imageOrientation: "landscape",
    },
    howToIntegrate: {
      title: "How To Integrate",
      content:
        "Integration with our booking system is straightforward. Start by including our JavaScript SDK in your project. Configure the SDK with your API credentials, then initialize the booking widget in your desired location. The widget is fully responsive and customizable to match your brand. Our RESTful API endpoints also allow for headless integration if you prefer to build a custom UI.",
      showImage: false,
      imageUrl: "",
      imageOrientation: "landscape",
    },
    bookingForm: {
      title: "Booking Form",
      content:
        "The booking form is the starting point of the reservation process. It captures essential trip details and preferences. Choose between one-way trips for point-to-point travel or hourly bookings for extended service with the same vehicle.",
      showImage: false,
      imageUrl: BookingFormOneway,
      imageOrientation: "landscape",
      imageDimensions: { width: 1477, height: 960 },
    },
    oneway: {
      title: "Oneway Booking",
      content:
        "Origin Location(From): Search and select from available cities, airports, or hotels. Destination(To): Search and select from available cities, airports, or hotels. Travel Date(Date): Select your preferred travel date. Travel Time(Time): Specify your desired pickup time. Time Will Automatically set based on Pickup Location which can further be changed based on convenience. The Booking date and time should be at least 24hr prior to travel date and time. Preferred Currency(Currency): Choose your billing currency by country or currency code. Number of Passengers(Pax): Select between 1-5 passengers (Max passenger allowed - 5 pax).",
      showImage: true,
      imageUrl: BookingFormOneway,
      imageOrientation: "landscape",
      imageDimensions: { width: 1477, height: 960 },
    },
    hourly: {
      title: "Hourly Booking",
      content:
        "Origin Location(From): Search and select from available cities, airports, or hotels. Duration: Search and select travel duration within 01 hour/20km to 24 hours/480km. Travel Date(Date): Select your preferred travel date. Travel Time(Time): Specify your desired pickup time. Time Will Automatically set based on Pickup Location which can further be changed based on convenience. The Booking date and time should be at least 24hr prior to travel date and time. Preferred Currency(Currency): Choose your billing currency by country or currency code. Number of Passengers(Pax): Select between 1-5 passengers.",
      showImage: true,
      imageUrl: BookingFormHourly,
      imageOrientation: "landscape",
      imageDimensions: { width: 1477, height: 960 },
    },
    vehicleSelection: {
      title: "Vehicle Selection",
      content:
        "The booking details section displays your selections: booking type (One-way/Hourly), Source, Destination(Only for Oneway), Distance, Duration, Travel date, Travel time, Currency, and Number of Passengers. Choose your vehicle from these five categories: Standard Sedan, Premium Sedan, Luxury Sedan, Economy Van, Premium Van.",
      showImage: true,
      imageUrl: VehicleSelection,
      imageOrientation: "landscape",
      imageDimensions: { width: 1477, height: 960 },
    },
    passengerDetails: {
      title: "Passenger Details",
      content:
        "Please complete the passenger information: Select your salutation, First name (Required), Last name (Required), Contact number with country code (Required), Email address (Required), Reference number (Required), Flight number (Optional), Special requests (Optional). Also you can see the summary of your booking, including vehicle details, booking information, and pricing. You must accept the terms and conditions, booking conditions, and privacy policy.",
      showImage: true,
      imageUrl: Passenger,
      imageOrientation: "landscape",
      imageDimensions: { width: 1477, height: 960 },
    },
    bookingSummary: {
      title: "Booking Summary",
      content:
        "Please review the following details carefully before proceeding with payment. Display Booking Summary Details. Display Passenger Details. Actions: Confirm & Pay Now: Proceed to the secure payment gateway to finalize your booking.",
      showImage: true,
      imageUrl: BookingSummary,
      imageOrientation: "landscape",
      imageDimensions: { width: 1477, height: 960 },
    },
    razorpayPayment: {
      title: "Payment",
      content:
        "During the Checkout Process: When you proceed to pay, you will be redirected to Payment Gateway interface. This interface will present you with payment method like: Credit/Debit Cards. Following the On-Screen Instructions: Payment Gateway will guide you through the process of entering your payment information securely for your chosen method. Confirmation: Once the payment is successful, you will usually receive a confirmation message on the screen and a Voucher will be sent via email. Please remember, never share your sensitive payment details (like card numbers, CVV, passwords) with anyone you don't trust or on unsecure platforms. Unfortunately, If your payment was unsuccessful. you have to create new booking from start.",
      showImage: true,
      imageUrl: Payment,
      imageOrientation: "landscape",
      imageDimensions: { width: 1477, height: 960 },
    },
    bookingConfirmation: {
      title: "Booking Confirmation",
      content:
        "Booking Confirmed! Thank you for your booking. You will shortly receive a detailed confirmation email at your registered email address. Key Details: Booking ID, Date/Time, Source & Destination Location, Type of Booking, Distance, Duration, No. of Pax, Passenger details, Vehicle details, Reference Number, Price details. Your Booking is Successful! A confirmation with complete booking details has been sent to your email address. We appreciate your business! If you don't receive your confirmation email within a few minutes, please check your spam folder or contact our support team at support@drivado.com. Thank You for Your Booking! Your payment has been successfully processed, and your booking is now confirmed.",
      showImage: false,
      imageUrl: "",
      imageOrientation: "landscape",
    },
  };

  // Scroll tracking function
  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = document.querySelectorAll("[data-section-id]");
      const scrollPosition = window.scrollY + 150;

      let foundActiveSection = false;
      [...sectionElements].reverse().forEach((section) => {
        const sectionId = section.getAttribute("data-section-id");
        const sectionTop = section.getBoundingClientRect().top + window.scrollY;
        const sectionBottom =
          sectionTop + section.getBoundingClientRect().height;

        if (
          scrollPosition >= sectionTop &&
          scrollPosition <= sectionBottom &&
          !foundActiveSection
        ) {
          foundActiveSection = true;
          setActiveSection(sectionId);

          sections.forEach((section) => {
            if (section.subSections) {
              const isSubSection = section.subSections.some(
                (sub) => sub.id === sectionId
              );
              if (isSubSection) {
                setExpandedSection(section.id);
              }
            }
          });
        }
      });
    };

    setTimeout(handleScroll, 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle clicking on a section link
  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false);

    // Smooth scroll to section
    const sectionElement = document.querySelector(
      `[data-section-id="${sectionId}"]`
    );
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Toggle expand/collapse for sections with subsections
  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // Render icon based on name
  const renderIcon = (iconName) => {
    switch (iconName) {
      case "home":
        return <HomeIcon className="w-5 h-5" />;
      case "key":
        return <KeyIcon className="w-5 h-5" />;
      case "code":
        return <CodeIcon className="w-5 h-5" />;
      case "filePlus":
        return <DocumentAddIcon className="w-5 h-5" />;
      case "car":
        return <TruckIcon className="w-5 h-5" />;
      case "user":
        return <UserIcon className="w-5 h-5" />;
      case "clipboardList":
        return <ClipboardCheckIcon className="w-5 h-5" />;
      case "creditCard":
        return <CreditCardIcon className="w-5 h-5" />;
      case "checkCircle":
        return <CheckCircleIcon className="w-5 h-5" />;
      default:
        return null;
    }
  };

  // Render the sidebar menu items

  const renderSidebarItems = () => {
    return sections.map((section) => {
      const hasSubSections =
        section.subSections && section.subSections.length > 0;
      const isActive = activeSection === section.id;
      const isExpanded = expandedSection === section.id;

      return (
        <div key={section.id} className="mb-1">
          <div
            className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer text-sm transition-all duration-200 ${
              isActive
                ? "bg-rose-500 text-white shadow-sm"
                : "hover:bg-gray-100 hover:bg-opacity-80"
            }`}
            onClick={() => {
              if (hasSubSections) {
                toggleSection(section.id);
              } else {
                handleSectionClick(section.id);
              }
            }}
          >
            <div className="flex items-center">
              <span
                className={`mr-2 ${isActive ? "text-white" : "text-gray-500"}`}
              >
                {renderIcon(section.icon)}
              </span>
              <span className="truncate font-medium">{section.label}</span>
            </div>
            {hasSubSections && (
              <span className={isActive ? "text-white" : "text-gray-500"}>
                {isExpanded ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </span>
            )}
          </div>

          {/* Subsections */}
          {hasSubSections && isExpanded && (
            <div className="ml-5 mt-1 space-y-1">
              {section.subSections.map((subSection) => (
                <div
                  key={subSection.id}
                  className={`px-3 py-2 rounded-lg cursor-pointer text-sm transition-all duration-200 ${
                    activeSection === subSection.id
                      ? "bg-rose-500 text-white shadow-sm"
                      : "hover:bg-gray-100 hover:bg-opacity-80"
                  }`}
                  onClick={() => handleSectionClick(subSection.id)}
                >
                  {subSection.label}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  // Render content sections - with conditional layout based on image orientation

  const renderContentSections = () => {
    return Object.entries(sectionContent).map(
      ([id, { title, showImage, imageUrl, imageOrientation }]) => (
        <div
          key={id}
          data-section-id={id}
          className="mb-12 scroll-mt-20"
          id={id}
        >
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <span className="w-1.5 h-8 bg-rose-500 rounded-full mr-2.5"></span>
            {title}
          </h2>
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
            {/* Conditional layout based on image orientation */}
            {showImage && imageUrl && imageOrientation === "landscape" && (
              <div className="mb-6">
                <div className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src={imageUrl}
                    alt={title}
                    className="w-full h-auto max-h-96 object-contain hover:scale-105 transition-transform duration-300"
                  />
                </div>
                {/* <p className="text-sm text-gray-500 mt-2 italic text-center">
                  Illustration for {title}
                </p> */}
              </div>
            )}

            {/* Portrait image layout - side by side with content */}
            {showImage && imageUrl && imageOrientation === "portrait" ? (
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                <div className="md:w-2/3">{formatContent(id)}</div>
                <div className="md:w-1/3">
                  <div className="rounded-lg overflow-hidden shadow-md">
                    <img
                      src={imageUrl}
                      alt={title}
                      className="w-full h-auto max-h-96 object-contain mx-auto hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2 italic text-center">
                    Illustration for {title}
                  </p>
                </div>
              </div>
            ) : (
              /* Regular content for landscape or no image */
              formatContent(id)
            )}
          </div>
        </div>
      )
    );
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="flex flex-col md:flex-row h-full bg-gray-50">
      {/* Mobile header */}
      <div className="md:hidden sticky top-0 z-20 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="bg-rose-500 text-white p-1.5 rounded">
            <svg
              className="w-5 h-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
            </svg>
          </div>
          <h1 className="text-lg font-bold">White Label</h1>
        </div>
        <button
          onClick={toggleMobileMenu}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={
                isMobileMenuOpen
                  ? "M6 18L18 6M6 6l12 12"
                  : "M4 6h16M4 12h16M4 18h16"
              }
            />
          </svg>
        </button>
      </div>

      {/* Mobile sidebar */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bottom-0 bg-white z-10 overflow-y-auto p-4">
          <nav className="space-y-1">{renderSidebarItems()}</nav>
        </div>
      )}

      {/* Documentation sidebar - adjust width to be narrower */}
      <div className="hidden md:block w-64 xl:w-72 h-screen bg-white border-r border-gray-200 p-4 sticky top-0 overflow-y-auto">
        <div className="flex items-center space-x-2 mb-6 px-3 py-2">
          <div className="bg-rose-500 text-white p-1.5 rounded">
            <svg
              className="w-5 h-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
            </svg>
          </div>
          <h1 className="text-lg font-bold">White Label</h1>
        </div>

        <nav className="space-y-1">{renderSidebarItems()}</nav>
      </div>
      {/* Main content - adjusted to take the remaining width */}
      <div className="w-full md:w-5/6 lg:w-5/6 overflow-y-auto p-6 md:p-8">
        {renderContentSections()}
      </div>
    </div>
  );
};

export default Documentation;
